import React, { useState, useRef, useEffect } from 'react';
import { Send, Mic, Paperclip } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

interface ChatInputProps {
  onSend: (message: string) => void;
  loading: boolean;
  placeholder?: string;
  className?: string;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  loading,
  placeholder = "Ask me anything about your marketing data...",
  className,
}) => {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value.trim() && !loading) {
      onSend(value.trim());
      setValue('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [value]);

  return (
    <div className={cn('p-6', className)}>
      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
        <div className="relative bg-white rounded-2xl shadow-lg border border-secondary-200/50 backdrop-blur-sm">
          {/* Input container */}
          <div className="flex items-end gap-3 p-4">
            {/* Attachment button */}
            <button
              type="button"
              className="flex-shrink-0 w-10 h-10 rounded-xl bg-secondary-100 hover:bg-secondary-200 flex items-center justify-center transition-colors duration-200 disabled:opacity-50"
              disabled={loading}
            >
              <Paperclip className="w-4 h-4 text-secondary-600" />
            </button>

            {/* Text input */}
            <div className="flex-1 relative">
              <textarea
                ref={textareaRef}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                disabled={loading}
                rows={1}
                className="w-full resize-none bg-transparent border-0 px-0 py-2 text-sm text-secondary-900 placeholder:text-secondary-500 focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ minHeight: '24px', maxHeight: '120px' }}
              />
              
              {/* Character count or typing indicator */}
              {value.length > 0 && (
                <div className="absolute bottom-0 right-0 text-xs text-secondary-400">
                  {value.length}
                </div>
              )}
            </div>

            {/* Voice input button */}
            <button
              type="button"
              className="flex-shrink-0 w-10 h-10 rounded-xl bg-secondary-100 hover:bg-secondary-200 flex items-center justify-center transition-colors duration-200 disabled:opacity-50"
              disabled={loading}
            >
              <Mic className="w-4 h-4 text-secondary-600" />
            </button>

            {/* Send button */}
            <Button
              type="submit"
              disabled={!value.trim() || loading}
              loading={loading}
              className={cn(
                "flex-shrink-0 h-10 px-4 rounded-xl transition-all duration-200",
                value.trim() && !loading
                  ? "bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 shadow-lg hover:shadow-xl"
                  : "bg-secondary-200 text-secondary-500"
              )}
            >
              <Send className="w-4 h-4" />
              <span className="sr-only">Send message</span>
            </Button>
          </div>

          {/* Quick suggestions */}
          {value.length === 0 && !loading && (
            <div className="px-4 pb-4">
              <div className="flex flex-wrap gap-2">
                {[
                  "Show sales trends",
                  "Top performing products",
                  "User demographics"
                ].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => setValue(suggestion)}
                    className="px-3 py-1.5 text-xs font-medium text-secondary-600 bg-secondary-50 hover:bg-secondary-100 rounded-lg transition-colors duration-200"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
};